import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import Config from '../api/Config';

import AstroshineLogo from '../assets/images/AshtroshineLogo.jpeg';
import PSLogo from '../assets/images/PSLogo.jpeg';
import zipZapCartLogo from '../assets/images/zipZapCartLogo.jpeg';
import EcoNariFoundationLogo from '../assets/images/EroNariFoundationLogo.jpeg';
import MockTestLogo from '../assets/images/mockTestLogo.jpeg';
import DestinyLogo from '../assets/images/DestinyLogo.jpeg';
/* ─── Constants ─── */

// Derive from the same source Config uses for its requests (mirrors
// AdminProjects.jsx) so this can never drift out of sync with the
// axios instance's baseURL, and so both files hit the same host.
const BASE_URL = (
  Config?.defaults?.baseURL ||
  process.env.REACT_APP_API_URL ||
  'https://berrybloomer.com'
).replace(/\/$/, '');
const DEFAULT_LINK = '';
const DEFAULT_IMAGE = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='400' height='300' fill='%231e293b'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%2364748b' font-size='14' font-family='sans-serif'%3ENo Image%3C/text%3E%3C/svg%3E`;

const encodeSegments = (path) =>
  path.replace(/^\/+/, '').split('/').map(encodeURIComponent).join('/');

/**
 * Returns an ordered list of URLs to try for a given project record.
 * Same logic as AdminProjects.jsx's buildImageCandidates: the backend
 * only confirmed serves the image at `/application/{id}/image`, keyed
 * off the project's own id — not a filename field on the list response.
 * We keep a couple of structurally different fallbacks and try each in
 * turn (see ProjectCard's onError below) until one actually loads.
 */
const buildImageCandidates = (project) => {
  const id = project?.id;
  if (id == null) return [DEFAULT_IMAGE];

  let origin = null;
  try {
    origin = new URL(BASE_URL).origin;
  } catch {
    origin = null;
  }

  const candidates = [];
  candidates.push(`${BASE_URL}/application/${id}/image`);
  candidates.push(`${BASE_URL}/webapp/application/${id}/image`);

  if (origin) {
    candidates.push(`${origin}/application/${id}/image`);
  }

  if (origin) {
    candidates.push(`${origin}/webapp/application/${id}/image`);
  }

  // In case a filePath/fileName field exists on the project record itself
  if (project?.filePath) {
    candidates.push(
      `${BASE_URL}/application/image/${encodeSegments(project.filePath)}`
    );
  }

  if (project?.fileName) {
    candidates.push(
      `${BASE_URL}/application/image/${encodeSegments(project.fileName)}`
    );
  }

  return candidates.length ? candidates : [DEFAULT_IMAGE];
};

/**
 * If the backend ever starts returning a genuine multi-photo `images`
 * array on a project record, use those real URLs (each normalized to
 * an absolute URL) and keep the carousel meaningful.
 *
 * Otherwise fall back to the single-image id-based candidate cascade above.
 */
const buildImageSlots = (project) => {
  if (Array.isArray(project?.images) && project.images.length) {
    const real = project.images
      .map((img) => {
        const raw =
          typeof img === 'string'
            ? img
            : (img?.url ?? img?.fileName ?? img?.filePath ?? '');

        if (!raw) return null;

        if (raw.startsWith('http://') || raw.startsWith('https://')) {
          return [raw];
        }

        return [`${BASE_URL}/application/image/${encodeSegments(raw)}`];
      })
      .filter(Boolean);

    if (real.length) {
      return real;
    }
  }

  // Single photo, resolved via the same cascade as AdminProjects.
  return [buildImageCandidates(project)];
};

/* ─── TEMPORARY HARD-CODED PROJECT DATA ─── */
const HARD_CODED_PROJECTS = [
  /* ─────────────── Parinay Sanskar APP ─────────────── */
  {
    id: 'parinay-sanskar-app',
    projectId: 'parinay-sanskar',
    title: 'Parinay Sanskar',
    cardType: 'APP',
    description:
      'A secure, AI-powered matrimony and matchmaking mobile application designed to connect prospective life partners with 100% verified profiles, intelligent preference matching, and private conversations.',
    link: 'https://play.google.com/store/apps/details?id=com.parinaysanskar',
    imageSlots: [[PSLogo]],
  },
  /* ─────────────── ZipZapCart APP ─────────────── */
  {
    id: 'zipzapcart-app',
    projectId: 'zipzapcart',
    title: 'ZipZapCart',
    cardType: 'APP',
    description:
      'A lightning-fast hyperlocal grocery and essentials delivery application featuring instant order processing, live real-time courier tracking, inventory sync, and seamless digital payments.',
    link: 'https://play.google.com/store/apps/details?id=com.user.zipzapcart',
    imageSlots: [[zipZapCartLogo]],
  },

  /* ─────────────── Mock Station APP ─────────────── */
  {
    id: 'mock-station-app',
    projectId: 'mock-station',
    title: 'Mock Station',
    cardType: 'APP',
    description:
      'A comprehensive mock test preparation platform offering date and year-wise mock tests, latest exam pattern-based questions, instant result analysis, and answer key checking — designed to help students ace competitive exams.',
    link: 'https://play.google.com/store/apps/details?id=com.mock.exam.app',
    imageSlots: [[MockTestLogo]],
  },

  /* ─────────────── ECO Nari Foundation WEB ─────────────── */
  {
    id: 'eco-nari-foundation-web',
    projectId: 'eco-nari-foundation',
    title: 'ECO Nari Foundation',
    cardType: 'WEB',
    description:
      'ECO Nari Foundation is a social impact platform focused on women empowerment, community development, environmental awareness, and social welfare initiatives.',
    link: 'https://www.econarifoundation.com/',
    imageSlots: [[EcoNariFoundationLogo]],
  },

  /* ─────────────── Ashtroshine APP ─────────────── */
  {
    id: 'ashtroshine-app',
    projectId: 'ashtroshine',
    title: 'Ashtroshine',
    cardType: 'APP',
    description:
      'A comprehensive astrology and spiritual consultation platform delivering personalized horoscope analysis, live consultations with certified astrologers, kundli matching, and daily spiritual guidance.',
    link: 'https://play.google.com/store/apps/details?id=com.astroshine.app',
    imageSlots: [[AstroshineLogo]],
  },

  /* ─────────────── Destiny Achievers WEB ─────────────── */
  {
    id: 'destiny-achievers-web',
    projectId: 'destiny-achievers',
    title: 'Destiny Achievers',
    cardType: 'WEB',
    description:
      'A numerology and spiritual guidance platform offering personal and business numerology consultations, spiritual products, and life-path insights — helping 40,000+ seekers find their destiny through numbers.',
    link: 'https://destinyachiievers.com/',
    imageSlots: [[DestinyLogo]],
  },
];

/* ─── Animations ─── */

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
`;

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 28,
  },

  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
};

/* ─── Styled Components ─── */

const Page = styled.div`
  font-family: 'Poppins', sans-serif;
  min-height: 100vh;
  padding: 80px 24px 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-x: hidden;
  color: #e2e8f0;
`;

const SectionLabel = styled.span`
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #00c4ff;
  margin-bottom: 12px;
`;

const Heading = styled(motion.h1)`
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 800;
  line-height: 1.15;
  background: linear-gradient(135deg, #fff 30%, #00c4ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0 0 16px;
  text-align: center;
`;

const SubHeading = styled(motion.p)`
  font-size: 0.95rem;
  color: #64748b;
  text-align: center;
  max-width: 480px;
  margin: 0 0 56px;
  line-height: 1.7;
`;

const ProjectGrid = styled.div`
  width: 100%;
  max-width: 1100px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 24px;
  padding: 0 8px;
  box-sizing: border-box;
`;

const ProjectGridItem = styled.div`
  flex: 0 0 calc(33.333% - 16px);
  max-width: calc(33.333% - 16px);
  box-sizing: border-box;
  display: flex;

  @media (max-width: 1000px) {
    flex: 0 0 calc(50% - 12px);
    max-width: calc(50% - 12px);
  }

  @media (max-width: 600px) {
    flex: 0 0 100%;
    max-width: 100%;
  }
`;

const Card = styled(motion.article)`
  width: 100%;
  min-height: 470px;
  background: rgba(13, 22, 45, 0.72);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.35);
  transition: all 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(135deg, #0047ff, #00c4ff);
    opacity: 0.8;
  }

  &:hover {
    border-color: rgba(0, 196, 255, 0.45);

    box-shadow:
      0 22px 50px rgba(0, 71, 255, 0.25),
      0 0 25px rgba(0, 196, 255, 0.2);

    transform: translateY(-8px);
  }
`;

/**
 * Fixed aspect ratio, always — this is what makes every card's image
 * area the same shape regardless of the source screenshot's own
 * dimensions.
 */
const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 26px 26px 0;
`;

const ImageArea = styled.div`
  position: relative;
  width: 88px;
  height: 88px;
  margin: 0;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  overflow: hidden;
`;

const ProjectPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  color: #38bdf8;
  background: rgba(0, 196, 255, 0.08);

  border: 1px solid rgba(0, 196, 255, 0.25);

  padding: 5px 12px;
  border-radius: 999px;

  white-space: nowrap;
`;

/**
 * Always absolutely positioned and object-fit: contain so project logos
 * remain visible without unnecessary cropping.
 */
const ProjectImg = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  position: absolute;
  inset: 0;
  object-fit: contain;
  border-radius: 16px;
  transition: transform 0.3s ease;

  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const ImgSkeleton = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, #1e293b 25%, #243044 50%, #1e293b 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s infinite linear;
`;

const ImgNav = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $side }) => $side}: 10px;
  z-index: 4;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition:
    opacity 0.2s,
    background 0.2s;

  ${ImageArea}:hover & {
    opacity: 1;
  }

  &:hover {
    background: rgba(0, 71, 255, 0.8);
  }
`;

const ImgCounter = styled.div`
  position: absolute;
  bottom: 10px;
  right: 12px;
  font-size: 0.72rem;
  color: #e2e8f0;
  background: rgba(0, 0, 0, 0.55);
  padding: 2px 8px;
  border-radius: 20px;
  backdrop-filter: blur(4px);
`;

const CardBody = styled.div`
  padding: 0 26px 26px;
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
`;

const CardTitle = styled.h2`
  font-size: 1.45rem;
  font-weight: 700;
  color: #ffffff;
  margin: 24px 0 12px;
  letter-spacing: -0.3px;
  line-height: 1.3;
`;

const CardDesc = styled.p`
  font-size: 0.92rem;
  line-height: 1.65;
  color: #94a3b8;
  margin: 0 0 22px;
  flex-grow: 1;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top: auto;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const ProjectLinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  width: fit-content;
  padding: 11px 24px;

  border-radius: 12px;

  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.16);

  color: #e2e8f0;
  text-decoration: none;

  font-size: 0.88rem;
  font-weight: 600;

  transition: all 0.25s ease;

  &:hover {
    background: rgba(0, 196, 255, 0.15);
    border-color: #00c4ff;
    color: #ffffff;
    box-shadow: 0 4px 14px rgba(0, 196, 255, 0.25);
    transform: translateY(-2px);
  }
`;

const DotRow = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;

const Dot = styled.button`
  width: ${({ $active }) => ($active ? '18px' : '6px')};
  height: 6px;
  border-radius: 3px;
  border: none;
  cursor: pointer;
  padding: 0;

  background: ${({ $active }) =>
    $active
      ? 'linear-gradient(90deg,#0047ff,#00c4ff)'
      : 'rgba(148,163,184,0.25)'};

  transition:
    width 0.3s ease,
    background 0.3s ease;
`;

const NavBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $side }) => $side}: 0;
  z-index: 10;

  width: 44px;
  height: 44px;

  border-radius: 50%;
  border: 1px solid rgba(0, 196, 255, 0.2);

  background: rgba(10, 17, 32, 0.85);
  color: #e2e8f0;

  font-size: 1.3rem;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  backdrop-filter: blur(8px);

  transition:
    background 0.2s,
    border-color 0.2s,
    opacity 0.2s;

  opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  &:hover {
    background: rgba(0, 71, 255, 0.5);
    border-color: rgba(0, 196, 255, 0.5);
  }
`;

const Status = styled.p`
  color: ${({ $error }) => ($error ? '#f87171' : '#475569')};
  font-size: 0.9rem;
  margin: 40px 0;
`;

const Skeleton = styled.div`
  border-radius: 20px;
  overflow: hidden;
  background: linear-gradient(160deg, #0f172a, #1e293b);
  border: 1px solid rgba(148, 163, 184, 0.06);

  .sk-img {
    aspect-ratio: 4/3;
    background: linear-gradient(90deg, #1e293b 25%, #243044 50%, #1e293b 75%);
    background-size: 800px 100%;
    animation: ${shimmer} 1.6s infinite linear;
  }

  .sk-body {
    padding: 20px 22px 22px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .sk-line {
    height: 12px;
    border-radius: 6px;
    background: linear-gradient(90deg, #1e293b 25%, #243044 50%, #1e293b 75%);
    background-size: 800px 100%;
    animation: ${shimmer} 1.6s infinite linear;
  }
`;

/* ─── ProjectCard ───
 *
 * `project.imageSlots` is an array of "slots" (one per real photo, or a
 * single slot when we only have the id-based fallback).
 *
 * Each slot is itself an array of candidate URLs to try in order.
 */
function ProjectCard({ project }) {
  const slots = project.imageSlots?.length
    ? project.imageSlots
    : [[DEFAULT_IMAGE]];

  const [slotIdx, setSlotIdx] = useState(0);
  const [attemptIdx, setAttemptIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const candidates = slots[slotIdx] ?? [DEFAULT_IMAGE];
  const src = candidates[attemptIdx] ?? DEFAULT_IMAGE;

  const hasLink = Boolean(project.link && project.link.trim());

  const goToSlot = (i) => {
    setLoaded(false);
    setAttemptIdx(0);
    setSlotIdx(i);
  };

  const prevImg = (e) => {
    e.stopPropagation();

    goToSlot((slotIdx - 1 + slots.length) % slots.length);
  };

  const nextImg = (e) => {
    e.stopPropagation();

    goToSlot((slotIdx + 1) % slots.length);
  };

  const handleError = () => {
    const next = attemptIdx + 1;

    if (next < candidates.length) {
      // Try the next candidate URL shape before giving up on this photo.
      setAttemptIdx(next);
      return;
    }

    // All candidates for this slot failed — show the placeholder but
    // still let it render (loaded=true) rather than spinning forever.
    setLoaded(true);
  };

  return (
    <Card variants={fadeUp} initial="hidden" animate="visible">
      <CardTop>
        <ImageArea>
          {!loaded && <ImgSkeleton />}

          <ProjectImg
            src={src}
            alt={`${project.title} ${project.cardType || 'Project'}`}
            $loaded={loaded}
            onLoad={() => setLoaded(true)}
            onError={handleError}
          />

          {slots.length > 1 && (
            <>
              <ImgNav $side="left" onClick={prevImg}>
                ‹
              </ImgNav>

              <ImgNav $side="right" onClick={nextImg}>
                ›
              </ImgNav>

              <ImgCounter>
                {slotIdx + 1} / {slots.length}
              </ImgCounter>
            </>
          )}
        </ImageArea>

        <ProjectPill>{project.cardType || 'Project'}</ProjectPill>
      </CardTop>

      <CardBody>
        <CardTitle>{project.title}</CardTitle>

        <CardDesc>{project.description}</CardDesc>

        <CardFooter>
          {hasLink && (
            <ProjectLinkButton
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {project.cardType === 'APP' ? '📱 APP Link' : '🌐 Website Link'}
            </ProjectLinkButton>
          )}

          {slots.length > 1 && (
            <DotRow>
              {slots.map((_, i) => (
                <Dot
                  key={i}
                  $active={i === slotIdx}
                  onClick={() => goToSlot(i)}
                />
              ))}
            </DotRow>
          )}
        </CardFooter>
      </CardBody>
    </Card>
  );
}

/* ─── SkeletonCard ─── */

function SkeletonCard() {
  return (
    <Skeleton>
      <div className="sk-img" />

      <div className="sk-body">
        <div className="sk-line" style={{ width: '60%' }} />

        <div className="sk-line" style={{ width: '90%' }} />

        <div className="sk-line" style={{ width: '75%' }} />
      </div>
    </Skeleton>
  );
}

/* ─── OurProjects (main) ─── */

export default function OurProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [index, setIndex] = useState(0);
  const [cols, setCols] = useState(3);

  const touchStartX = useRef(0);

  /* ─── Responsive cols ─── */

  useEffect(() => {
    const calc = () => {
      if (window.innerWidth < 640) {
        setCols(1);
      } else if (window.innerWidth < 1024) {
        setCols(2);
      } else {
        setCols(3);
      }
    };

    calc();

    window.addEventListener('resize', calc);

    return () => {
      window.removeEventListener('resize', calc);
    };
  }, []);

  /* ─── TEMPORARY: LOAD HARD-CODED PROJECTS ─── */

  useEffect(() => {
    setProjects(HARD_CODED_PROJECTS);
    setLoading(false);
    setError(null);
  }, []);

  /* ─── API CODE — PRESERVED AND COMMENTED FOR NOW ─── */

  /*
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data } = await Config.get('/application');

        if (!Array.isArray(data)) {
          throw new Error('Bad response');
        }

        // Log the raw shape once so you can confirm the actual image
        // field name / id in the console, same as AdminProjects.jsx does.
        if (data[0]) {
          console.log(
            '[OurProjects] sample project record:',
            data[0]
          );
        }

        if (!cancelled) {
          setProjects(
            data.map((item) => ({
              id: item.id,
              title:
                item.name ||
                item.title ||
                'Untitled',
              description:
                item.description || '',
              link:
                item.link || '',
              imageSlots:
                buildImageSlots(item),
            }))
          );
        }
      } catch (err) {
        console.error(err);

        if (!cancelled) {
          setError(
            'Could not load projects.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);
  */

  /* ─── Navigation ─── */

  const maxIndex = Math.max(0, projects.length - cols);

  const prev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  const next = useCallback(
    () => setIndex((i) => Math.min(maxIndex, i + 1)),
    [maxIndex]
  );

  /* ─── Touch swipe ─── */

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    const dx = touchStartX.current - e.changedTouches[0].clientX;

    if (dx > 50) {
      next();
    }

    if (dx < -50) {
      prev();
    }
  };

  const skeletons = Array.from({
    length: cols,
  });

  return (
    <Page>
      <SectionLabel>Our Work</SectionLabel>

      <Heading
        initial={{
          opacity: 0,
          y: -16,
        }}
        animate={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          duration: 0.5,
        }}
      >
        Portfolio
      </Heading>

      <SubHeading
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 1,
        }}
        transition={{
          delay: 0.15,
          duration: 0.5,
        }}
      >
        A selection of projects we've designed and built.
      </SubHeading>

      {error && <Status $error>{error}</Status>}

      {!error && (
        <ProjectGrid>
          {loading
            ? skeletons.map((_, i) => (
                <ProjectGridItem key={i}>
                  <SkeletonCard />
                </ProjectGridItem>
              ))
            : projects.map((p) => (
                <ProjectGridItem key={p.id ?? p.title}>
                  <ProjectCard project={p} />
                </ProjectGridItem>
              ))}
        </ProjectGrid>
      )}

      {!loading && !error && projects.length === 0 && (
        <Status>No projects to show yet.</Status>
      )}
    </Page>
  );
}